/**
 * BottomNav — the v2 floating tab bar.
 *
 * Ported from AppScreen.dc.html ("OnFlow Design System" project): a glass slab
 * that floats above the content rather than a bar welded to the bottom edge.
 * Active tab is an ember wash, never a status colour.
 *
 * Tabs map onto the template's five screens:
 *   Home → /dashboard   Diet → /log   Train → /workout/new
 *   Review → /review    You → /profile
 *
 * Diet and Train are gated on the tracking modes chosen in onboarding. The
 * mode step asks "what do you want to track? choose one or both", so showing
 * both tabs regardless made that choice look like it did nothing — a
 * training-only user landed on a Diet tab with no targets and no plan.
 * Home, Review, and You are always present: Review renders its own
 * nutrition-is-off state, and You is where the choice gets changed.
 */
import type { TrackingMode } from '@onflow/shared';
import { NavLink, useLocation } from 'react-router-dom';

import { useOnboardingProfile } from '../hooks/useOnboarding.js';

import { Icon, type IconName } from './v2/Icon.js';

interface Tab {
  to: string;
  /** Path prefix that lights the tab — broader than `to` so sub-routes match. */
  match: string;
  label: string;
  icon: IconName;
  /** When set, the tab renders only if this mode is enabled. */
  requiresMode?: TrackingMode;
}

const TABS: ReadonlyArray<Tab> = [
  { to: '/dashboard', match: '/dashboard', label: 'Home', icon: 'home' },
  { to: '/log', match: '/log', label: 'Diet', icon: 'diet', requiresMode: 'nutrition' },
  {
    to: '/workout/new',
    match: '/workout',
    label: 'Train',
    icon: 'dumbbell',
    requiresMode: 'training',
  },
  { to: '/review', match: '/review', label: 'Review', icon: 'chart' },
  { to: '/profile', match: '/profile', label: 'You', icon: 'user' },
];

export function BottomNav() {
  const location = useLocation();
  const profile = useOnboardingProfile();

  // Default to both while the profile is in flight and for legacy rows that
  // predate the mode step — hiding a tab on a slow request would make the bar
  // visibly reflow under the user mid-tap.
  const modes = (profile.data?.trackingModes as TrackingMode[] | undefined) ?? [
    'nutrition',
    'training',
  ];
  const tabs = TABS.filter((tab) => !tab.requiresMode || modes.includes(tab.requiresMode));

  return (
    <nav aria-label="Primary" className="v2-nav">
      {tabs.map((tab) => {
        const active = location.pathname.startsWith(tab.match);
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={active ? 'v2-nav-tab v2-nav-tab--active' : 'v2-nav-tab'}
          >
            <Icon name={tab.icon} size={21} strokeWidth={active ? 2.2 : 1.9} />
            {tab.label}
          </NavLink>
        );
      })}
    </nav>
  );
}
